import { Button, Card, Flex, Tooltip } from '@mantine/core';
import { IconUserCircle, IconX } from '@tabler/icons-react';
import { Provider } from 'interfaces';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Section, SectionProps } from './Section';

import { useGetModalSectionBorderColor } from 'hooks/useGetModalSectionBorderColor';
import { ProviderSelect } from 'components/ProviderSelect';
import { useActiveForm } from 'pages/claims/components';
import { getValueByPath } from 'utils/getValueByPath';

interface ClaimProviderSectionProps extends SectionProps {
  providers: Provider[];
}

export const ClaimProviderSection = ({
  index,
  title,
  keys,
  cols,
  providers,
  editComponentsMap,
  type = 'edit',
}: ClaimProviderSectionProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const { form, isBulk, prefix } = useActiveForm();
  const borderColor = useGetModalSectionBorderColor({
    keys,
  });

  const providerPath = prefix ? `${prefix}.provider` : 'provider';
  const providerIDPath = prefix ? `${prefix}.providerID` : 'providerID';

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );

  useEffect(() => {
    setSelectedProvider(getValueByPath(form.getValues(), providerPath) || null);
  }, [prefix]);

  const resetProvider = () => {
    setSelectedProvider(null);
    const providerPath = prefix ? `${prefix}.provider` : 'provider';
    const providerIDPath = prefix ? `${prefix}.providerID` : 'providerID';
    form.setFieldValue(providerPath, null);
    form.setFieldValue(providerIDPath, null);
    requestAnimationFrame(() => cardRef.current?.focus());
  };

  const icon = <IconUserCircle size={20} />;

  return (
    <Card
      ref={cardRef}
      tabIndex={-1}
      padding={'md'}
      shadow="xs"
      radius="md"
      withBorder
      display={'flex'}
      w={'100%'}
      style={{
        borderColor: borderColor,
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
    >
      {selectedProvider ? (
        <Section
          index={index}
          title={title}
          icon={icon}
          keys={
            selectedProvider?.groups?.length >= 1
              ? keys
              : keys.filter((key) => key !== 'groupID')
          }
          form={form}
          cols={cols}
          editComponentsMap={editComponentsMap}
          type={type}
        />
      ) : isBulk ? (
        <BulkClaimProviderSection
          providers={providers}
          setSelectedProvider={setSelectedProvider}
          cardRef={cardRef}
        />
      ) : (
        <ProviderSelect
          providers={providers}
          value={
            (getValueByPath(form.getValues(), providerIDPath) as string) || null
          }
          onChange={(value, provider) => {
            if (!value) {
              resetProvider();
              return;
            }
            const providerPath = prefix ? `${prefix}.provider` : 'provider';
            const providerIDPath = prefix
              ? `${prefix}.providerID`
              : 'providerID';
            form.setFieldValue(providerIDPath, value);
            form.setFieldValue(providerPath, provider);
            requestAnimationFrame(() => {
              cardRef.current?.focus();
              setSelectedProvider(provider || null);
            });
          }}
          radius={'md'}
          w={'100%'}
        />
      )}
      {selectedProvider && (
        <Flex justify={'center'} w="2%" gap={'lg'} mb={'md'}>
          <Tooltip label="Change Provider" position="top">
            <Button variant="subtle" w={'100%'} p={0} c="gray">
              <IconX size={20} onClick={resetProvider} />
            </Button>
          </Tooltip>
        </Flex>
      )}
    </Card>
  );
};

const BulkClaimProviderSection = ({
  providers,
  setSelectedProvider,
  cardRef,
}: Pick<ClaimProviderSectionProps, 'providers'> & {
  setSelectedProvider?: Dispatch<SetStateAction<Provider | null>>;
  cardRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { form, prefix } = useActiveForm();

  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <ProviderSelect
      providers={providers}
      ref={inputRef}
      value={
        (getValueByPath(
          form.getValues(),
          prefix ? `${prefix}.providerID` : 'providerID'
        ) as string) || null
      }
      onChange={(value, provider) => {
        const providerPath = prefix ? `${prefix}.provider` : 'provider';
        const providerIDPath = prefix ? `${prefix}.providerID` : 'providerID';
        if (!value) {
          form.setFieldValue(providerIDPath, null);
          form.setFieldValue(providerPath, null);
          requestAnimationFrame(() => cardRef?.current?.focus());
          setSelectedProvider(null);
          return;
        }
        form.setFieldValue(providerIDPath, value);
        form.setFieldValue(providerPath, provider || null);
        requestAnimationFrame(() => {
          cardRef?.current?.focus();
          setSelectedProvider?.(provider || null);
        });
      }}
      radius={'md'}
      w={'100%'}
    />
  );
};
